/* Persona-capacity → landing route seam (persona-foundation Task 3).
 *
 * A signed-in user can hold multiple "capacities" — a (persona, department)
 * pair, where `department` is only meaningful when `persona === 'management'`
 * (a user can be, e.g., both CEO — management/company — and head of bizdev —
 * management/bizdev). Backend contract: GET /auth/me → {user:
 * {..., personas: [{persona, department}], primary_persona}} (service/app's
 * user_personas table — see docs/superpowers/specs/2026-07-11-persona-
 * foundation-design.md).
 *
 * PERSONA_ROUTES is the SEAM: capacity string → an existing hub destination.
 * Foundation wires every entry to a real view that already exists (verified
 * below); a future dedicated surface (bizdev prospect dashboard, writer
 * content queue, management department rollup) replaces one entry here
 * without touching resolveLanding/boot()/the capacity switcher.
 */

export type PersonaRow = { persona: string; department: string };
export type PersonaUser = { personas?: PersonaRow[]; primary_persona?: string | null };

// capacity string: 'am' | 'bizdev' | 'writer' | 'management:<dept>'
export function capacityKey(persona: string, department: string): string {
  return persona === 'management' ? `management:${department}` : persona;
}

export function heldCapacities(u: PersonaUser): string[] {
  return (u.personas ?? []).map((p) => capacityKey(p.persona, p.department));
}

/* Human-readable label for a capacity string: "persona name" for a plain
 * capacity, dept for management ("CEO" for the company-wide hat, "head of
 * <dept>" for a departmental one) per the design spec (docs/superpowers/
 * specs/2026-07-11-persona-foundation-design.md "Routing + capacity
 * switcher"). Shared between ws/board.ts's capacity switcher and
 * ws/adminUsers.ts's editor (both the primary-persona picker and the
 * per-department checkbox labels) — moved here from board.ts (Task 4) so
 * adminUsers.ts can reuse it without a circular import (board.ts already
 * imports openUsersPanel from adminUsers.ts). */
export function capacityLabel(capacity: string): string {
  if (capacity.startsWith('management:')) {
    const dept = capacity.slice('management:'.length);
    return dept === 'company' ? 'CEO' : `head of ${dept}`;
  }
  return capacity;
}

/* SEAM RE-POINTED for the 3-area shell (shell restructure, spec A5.4): the
 * ONE edit the shell chunk makes here. Each value is now a symbolic router
 * ROUTE (lib/router.ts), not a raw section anchor — this map is the single
 * place persona → landing is named, so a future dedicated surface re-points
 * one entry without touching resolveLanding/boot()/board.ts's switcher.
 *
 * TARGET VERIFICATION — every route below resolves in lib/router.ts:
 *  - '#/work' is the DEFAULT area (the board / triage, §7) — where am + bizdev
 *    both land now (the audit's "work first, report one click away" ruling:
 *    bizdev moves from report-first to work-first, spec A1.4).
 *  - '#/portfolio' is the multi-client home; the router redirects it to the
 *    portfolio MPA entry (hub/portfolio.html → src/portfolio.ts) in step 1 —
 *    the management surface (unchanged behavior, symbolic route).
 *  - '#/work?cat=content' is the writer landing per spec A1.4. The router
 *    parses it as the Work area today (the `?cat=content` content-category
 *    filter is a no-op until a later chunk wires that filter kind — same
 *    honest degradation the old '#s7' writer target had; see the TODO below).
 */
export const PERSONA_ROUTES: Record<string, string> = {
  // Client-first IA increment 1 (spec §6, §7-step-1): am + management land on
  // My Clients (#/clients — the new login landing). bizdev stays work-first;
  // writer stays on the content-scoped Work route. This is the one-edit remap.
  am: '#/clients',                 // My Clients (was #/work)
  bizdev: '#/work',                // work-first (was report-first pre-restructure)
  // TODO(persona-surface): wire a content-category filter kind so the router's
  // `?cat=content` (parsed but not yet applied) actually pre-filters the board.
  // For now this lands writer on the same unfiltered Work board as `am`.
  writer: '#/work?cat=content',
  'management:company': '#/clients',  // was #/portfolio
  'management:bizdev': '#/clients',
  'management:am': '#/clients',
  'management:writer': '#/clients',
};

const CAP_KEY = 'fb-capacity';

export function setActiveCapacity(cap: string): void {
  localStorage.setItem(CAP_KEY, cap);
}

export function activeCapacity(u: PersonaUser): string | null {
  const held = heldCapacities(u);
  const stored = localStorage.getItem(CAP_KEY);
  if (stored && held.includes(stored)) return stored;
  if (u.primary_persona && held.includes(u.primary_persona)) return u.primary_persona;
  return held[0] ?? null;
}

export function resolveLanding(u: PersonaUser): string | null {
  const cap = activeCapacity(u);
  return cap ? (PERSONA_ROUTES[cap] ?? null) : null;
}

/* Boot-time guard (persona-foundation Task 3 review fix): persona-landing may
 * only apply on a "bare" arrival — no hash fragment AND no `?task=` query
 * param. The original guard only special-cased `?task=` and `#sN`, missing
 * the third shipped deep-link form `#notes` (ws/taskLink.ts's
 * openTaskDeepLink, emitted by notifBell.ts for client-note @mention
 * notifications) — a persona-holding user clicking that notification got
 * silently rerouted before `#notes` was ever honored. Generalizing to "any
 * hash or ?task= means something specific is targeted" covers `#notes` and
 * any future hash deep-link without needing another special case here. */
export function shouldApplyLanding(hash: string, search: string): boolean {
  return !hash && !new URLSearchParams(search).has('task');
}
